import {PriorityConfig} from '@aboutyou/data-fetcher';
import {Coupon} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {GetStreamResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coupons/v1/landingpage/service.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {Grid, ProductImageType} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {RequestFailure} from '../../../features/Coupons/CouponWallet/deprecated/RequestFailure/RequestFailure';
import {getCouponValues} from '../../../features/Coupons/utils/getCouponValues';
import {hasAppOnlyRedeemableDevices} from '../../../features/Coupons/utils/hasAppOnlyRedeemableDevices';
import {StreamTileListProps} from '../../../features/ProductStream/StreamTileList';
import {TilesByPageOffsetLimit} from '../../../features/ProductStream/TilesByPageOffsetLimit';
import {
  StreamPaddingCSS,
  labelToProductImageType,
} from '../../../features/ProductStream/helpers';
import {useProductStreamStatus} from '../../../features/ProductStream/utils';
import {ProductStreamOffsetLimitPagination} from '../../../features/ProductStreamPagination/ProductStreamOffsetLimitPagination';
import {LoadMoreComponentDisplayConfig} from '../../../features/ProductStreamPagination/ProductStreamTokenPagination';
import {usePaginationRange} from '../../../features/ProductStreamPagination/utils/usePaginationRange';
import {ProductTileProps} from '../../../features/ProductTile/ProductTile';
import {useShallowRenderingState} from '../../../features/ProductTile/utils';
import {StreamEmpty} from './components/StreamEmpty';
import {useCouponLandingPageStream} from './useCouponLandingPageStream';
import {usePreventProductTilesSwipeAnimation} from './usePreventProductTilesSwipeAnimation';

interface CouponProductStreamProps extends StreamTileListProps, PriorityConfig {
  preferredProductImageType?: ProductImageType;
  loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig;
  onClickAddCta?: ({
    productId,
    filteredSizeId,
  }: {
    productId: number;
    filteredSizeId?: number;
  }) => void;
  coupon?: Coupon;
  ProductTileComponent?: React.FC<ProductTileProps>;
}

const HISTORY_STATE_CURRENT_PAGE_KEY = 'coupon_product_stream_current_page';
const HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY =
  'coupon_product_stream_last_visible_items';

export const CouponLandingPageProductStream: React.FC<
  CouponProductStreamProps
> = ({
  gridColumns,
  preferredProductImageType,
  loadMoreComponentDisplayConfig,
  renderingPriority,
  onClickAddCta,
  interactiveHoverMode,
  productTileGalleryMode,
  enableImageGalleryAnimation,
  coupon,
  ProductTileComponent,
}) => {
  const [currentPage, setCurrentPage] = useHistoryPageState(1, {
    uniqueKey: HISTORY_STATE_CURRENT_PAGE_KEY,
  });
  const preventProductTilesSwipeAnimation =
    usePreventProductTilesSwipeAnimation();

  const productStreamRequest = useCouponLandingPageStream;
  const {
    isNextPageReady,
    totalPages,
    isStreamEmpty,
    data: resolvedRequestData,
  } = useProductStreamStatus(currentPage, productStreamRequest);

  const paginationRange = usePaginationRange({
    initialPage: 1,
    currentPage,
  });
  const shallowRenderingState = useShallowRenderingState({
    uniqueKey: HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY,
  });

  const {promotionId, redeemableDevices} = getCouponValues(coupon);

  return (
    <section>
      <StyledGrid
        columns={gridColumns}
        enableEnhancedTileRendering
        uniqueKey="CouponProductStream"
      >
        {paginationRange.map(page => (
          <TilesByPageOffsetLimit
            ProductTileComponent={ProductTileComponent}
            request={productStreamRequest}
            key={page}
            page={page}
            gridColumns={gridColumns}
            preferredProductImageType={labelToProductImageType(
              preferredProductImageType,
            )}
            renderingPriority={renderingPriority}
            shallowRenderingState={shallowRenderingState}
            onClickAddCta={onClickAddCta}
            disableImagePreloading
            interactiveHoverMode={interactiveHoverMode}
            productTileGalleryMode={productTileGalleryMode}
            enableImageGalleryAnimation={
              enableImageGalleryAnimation && !preventProductTilesSwipeAnimation
            }
            errorComponent={RequestFailure}
            selectedCoupon={
              hasAppOnlyRedeemableDevices(redeemableDevices || [])
                ? undefined
                : promotionId
            }
          />
        ))}
      </StyledGrid>
      {isNextPageReady && (
        <ProductStreamOffsetLimitPagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
          totalPages={totalPages}
        />
      )}

      {isStreamEmpty && (
        <StreamEmpty requestData={resolvedRequestData as GetStreamResponse} />
      )}
    </section>
  );
};

const StyledGrid = styled(Grid)`
  ${StreamPaddingCSS}
`;
